import {useFieldContext} from '../../integrations/form/index.js'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {
  Box,
  Combobox as ChakraCombobox,
  ComboboxRootProps,
  createListCollection,
  Field,
  InputGroup,
  Portal,
  Span,
  useFilter,
} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useMemo, useState, useCallback, useEffect} from 'react'
import {LuSearch} from 'react-icons/lu'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'
import {buildSelectionLabel} from './selection-label.js'

export interface ComboboxProps extends Omit<ComboboxRootProps, 'collection'> {
  items: SelectItem[]
  label: string
  hideLabel?: boolean
  /** Hides the leading magnifying glass on the control (the search still works). */
  hideSearchIcon?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  autoComplete?: string
  controlProps?: ChakraCombobox.ControlProps
  contentProps?: ChakraCombobox.ContentProps
  inputProps?: ChakraCombobox.InputProps
  portalled?: boolean
  disabled?: boolean
}

export function Combobox({required, hideSearchIcon, ...props}: ComboboxProps) {
  const field = useFieldContext<string | string[]>()
  const {t} = useTranslate('common')
  const {contains} = useFilter({sensitivity: 'base'})
  const [filter, setFilter] = useState('')
  const [inputValue, setInputValue] = useState<string | undefined>(undefined)
  const [focused, setFocused] = useState(false)

  const isSingleMode = 'mode' in field.options && field.options.mode === 'value'

  let value: string[] = []
  if (field.state.value) {
    value = Array.isArray(field.state.value) ? field.state.value : [field.state.value]
  }

  // in multi-select mode the input is also the search field, so the chosen labels are shown back on change
  const selectedLabels = value
    .map(selected => props.items.find(item => item.value === selected)?.label)
    .filter((label): label is string => Boolean(label))
  const selectedText = buildSelectionLabel(selectedLabels[0], selectedLabels.length - 1, t('more'))

  useEffect(() => {
    if (!isSingleMode) setInputValue(selectedText)
  }, [isSingleMode, selectedText])

  const collection = useMemo(() => {
    let filteredItems = props.items
    if (filter) {
      filteredItems = props.items.filter(item => contains(item.label, filter))
    }

    if (filteredItems.length === 0) {
      return createListCollection({
        items: [
          {
            label: t('no_options_available'),
            value: 'EMPTY_STATE',
          },
        ] as SelectItem[],
        isItemDisabled: item => item.value === 'EMPTY_STATE',
      })
    }

    return createListCollection({items: filteredItems})
  }, [props.items, contains, filter, t])

  const handleValueChange = useCallback(
    (e: ChakraCombobox.ValueChangeDetails) => {
      // handle input clear (x button)
      if (e.value.length === 0) {
        setFilter('')
        field.handleChange(isSingleMode ? '' : [])
        return
      }

      // guard against dummy item
      if (e.value[0] === 'EMPTY_STATE') return

      // handle standard selection
      field.handleChange(isSingleMode ? e.value[0] : e.value)
      // reset the search so the next open lists every option again
      if (!isSingleMode) setFilter('')
    },
    [field, isSingleMode]
  )

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraCombobox.Root
        width="100%"
        collection={collection}
        name={field.name}
        value={value}
        disabled={props.disabled}
        inputValue={isSingleMode ? undefined : inputValue}
        onInputValueChange={e => {
          // Chakra clears the search input on blur; keep showing the selection instead of the placeholder
          if (!isSingleMode && (focused || e.inputValue !== '')) setInputValue(e.inputValue)
          setFilter(e.inputValue)
        }}
        onValueChange={handleValueChange}
        openOnClick
        {...props}
        positioning={{sameWidth: true, ...props.positioning}}
      >
        <ChakraCombobox.Label srOnly={props.hideLabel}>
          {props.label} {required && <FieldRequiredIndicator />}
        </ChakraCombobox.Label>

        <ChakraCombobox.Control backgroundColor="bg" {...props.controlProps}>
          <InputGroup startElement={hideSearchIcon ? undefined : <LuSearch />}>
            <ChakraCombobox.Input
              placeholder={props.placeholder}
              autoComplete={props.autoComplete}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                setFocused(false)
                field.handleBlur()
                if (!isSingleMode) setInputValue(selectedText)
              }}
              {...props.inputProps}
            />
          </InputGroup>
          <ChakraCombobox.IndicatorGroup>
            <ChakraCombobox.ClearTrigger />
            <ChakraCombobox.Trigger />
          </ChakraCombobox.IndicatorGroup>
        </ChakraCombobox.Control>

        <Portal disabled={!props.portalled}>
          <ChakraCombobox.Positioner width="100%">
            <ChakraCombobox.Content minWidth="20rem" {...props.contentProps}>
              {collection.items?.map(item => {
                const isEmptyState = item.value === 'EMPTY_STATE'

                return (
                  <ChakraCombobox.Item key={item.value} item={item}>
                    <Box display="flex" flexDirection="column">
                      <Span wordBreak="break-all" truncate>
                        {item.label}
                      </Span>
                      {!isEmptyState && item.description !== undefined && (
                        <Span fontSize="xs" color="fg.muted">
                          {item.description}
                        </Span>
                      )}
                    </Box>
                    {!isEmptyState && <ChakraCombobox.ItemIndicator />}
                  </ChakraCombobox.Item>
                )
              })}
            </ChakraCombobox.Content>
          </ChakraCombobox.Positioner>
        </Portal>
      </ChakraCombobox.Root>

      {props.helpText && <Field.HelperText textStyle="sm">{props.helpText}</Field.HelperText>}

      <ErrorMessage />
    </FieldRoot>
  )
}
